import type { Contract } from '~/data/schemas/contract';

import type {
  ContractReview,
  ContractReviewCarKpi,
  ContractReviewCustomDetail,
  ContractReviewRuleCategory,
  ExtractionVersion,
  MaintenanceUnit,
} from './types';

const REVIEW_RULE_CATEGORIES: ContractReviewRuleCategory[] = [
  'review-rule',
  'included-service',
  'excluded-service',
  'miscellaneous-charge',
  'other-term',
];

const toReviewRuleCategory = (value: string | undefined): ContractReviewRuleCategory => {
  if (value && (REVIEW_RULE_CATEGORIES as string[]).includes(value)) {
    return value as ContractReviewRuleCategory;
  }
  return 'review-rule';
};

const toExtractionVersion = (value: string | null | undefined): ExtractionVersion => {
  if (value === 'v0' || value === 'v1') return value;
  return null;
};

const toMaintenanceUnit = (value: string | undefined): MaintenanceUnit =>
  value === 'hours' ? 'hours' : 'visits';

const mapCustomDetails = (customDetails: Contract['customDetails']): ContractReviewCustomDetail[] =>
  (customDetails ?? [])
    .map(({ label, name, value }): ContractReviewCustomDetail | null => {
      const resolvedLabel = label || name;
      if (!resolvedLabel) return null;
      return { label: resolvedLabel, value: value ?? '' };
    })
    .filter((d): d is ContractReviewCustomDetail => d !== null);

export const mapContract = (contract: Contract): ContractReview => {
  const cancellationDays = (() => {
    if (!contract.cancellation) return null;
    const parsed = parseInt(contract.cancellation, 10);
    return Number.isFinite(parsed) ? parsed : null;
  })();

  const properties = (contract.propertyContracts ?? []).map((pc) => {
    const cars: ContractReviewCarKpi[] = pc.cars.map((car) => ({
      carId: car.carId,
      carName: car.name || 'Car',
      maintenanceQuantity: Number(car.maintenanceQuantity),
      maintenanceUnit: toMaintenanceUnit(car.maintenanceUnit),
      maintenanceTimePeriodMonths: Number(car.maintenanceTimePeriod),
      regularCallbackRate: Number(car.regularCallbackRate),
      emergencyCallbackRate: Number(car.emergencyCallbackRate),
      regularCallbackResponseTime: Number(car.regularCallbackResponseTime),
      emergencyCallbackResponseTime: Number(car.emergencyCallbackResponseTime),
    }));

    return {
      id: pc.propertyId,
      propertyContractId: pc.id,
      rateSetId: pc.rateSetId,
      name: pc.propertyName,
      address: pc.propertyAddress ?? '',
      carCount: pc.carCount,
      monthlyPrice: pc.monthlyPrice ? Number(pc.monthlyPrice) : 0,
      // Nullable dates carry through as null (never '') so consumers render a
      // placeholder instead of feeding '' into daysUntil → NaN (AMP-585).
      startDate: pc.startDate ?? null,
      expirationDate: pc.expirationDate ?? null,
      cars,
    };
  });

  return {
    id: contract.id,
    name: contract.name,
    vendor: contract.serviceProvider?.name ?? 'Unknown',
    type: contract.type,
    properties,
    // Nullable dates carry through as null (never '') so consumers render a
    // placeholder instead of feeding '' into daysUntil → NaN (AMP-585).
    startDate: contract.startDate ?? null,
    expirationDate: contract.expirationDate ?? null,
    term: contract.term ?? '',
    monthlyPriceTaxInclusive: contract.monthlyPricingTaxInclusive ?? null,
    escalationRate: Number(contract.annualEscalationRate ?? 0),
    cancellationDays,
    hourlyFeeAdjustmentRate: Number(contract.hourlyFeeAdjustmentRate ?? 0),
    partsAndMaterialsMarkup: Number(contract.partsAndMaterialsMarkup ?? 0),
    contractPaper: contract.contractPaper ?? '',
    businessHours: contract.businessHours ?? '',
    phoneMonitoring: contract.phoneMonitoring ?? null,
    fireService: contract.fireService ?? null,
    pressureTest: contract.pressureTest ?? null,
    aiReviewEligible: contract.aiReviewEligible,
    canChat: contract.canChat,
    extractionVersion: toExtractionVersion(contract.extractionVersion),
    extractionAppliedAt: contract.extractionAppliedAt ?? null,
    reviewRules: contract.reviewRules.map((r) => ({
      id: r.id ?? `${contract.id}-${r.reviewRule}`,
      rule: r.reviewRule,
      category: toReviewRuleCategory(r.category),
    })),
    kpiTargets: {
      maintenanceCompletionTarget: Number(contract.maintenanceCompletionTarget ?? 0),
      maintenanceHoursTarget: Number(contract.maintenanceHoursTarget ?? 0),
      uptimeTarget: Number(contract.uptimeTarget ?? 0),
      onTimeResponseTargetPct: Number(contract.onTimeResponseTimeTargetPct ?? 0),
      callbackRateTarget:
        contract.callbackRateTarget != null ? Number(contract.callbackRateTarget) : null,
      emergencyCallRateTarget:
        contract.emergencyCallRateTarget != null ? Number(contract.emergencyCallRateTarget) : null,
    },
    rateSets: contract.rateSets.map((rs) => ({
      id: rs.id,
      name: rs.name,
      // `description` is optional on the generated rate-set wire shape; the review
      // view model treats it as a plain string, so default an absent one to ''.
      description: rs.description ?? '',
      rates: rs.rates.map((r) => ({
        workerType: r.workerType,
        workType: r.workType,
        rateType: r.rateType,
        hourlyRate: Number(r.hourlyRate),
        coverageType: r.coverageType,
        overtimeMultiplier: r.overtimeMultiplier != null ? Number(r.overtimeMultiplier) : null,
        isDefault: r.isDefault,
        description: r.description ?? '',
      })),
    })),
    attachments: contract.attachments.map((a) => ({
      id: a.id,
      // attachmentUrl is nullable on the wire (aligned with the generated read);
      // fall back to a generic name when there's no URL to derive one from.
      fileName: a.name ?? a.attachmentUrl?.split('/').pop() ?? 'attachment',
      isPrimary: a.isPrimary,
      isSummarySheet: a.isSummarySheet,
      hasTextLayer: a.hasTextLayer ?? true,
      uploadedAt: a.uploadedAt ?? '',
      // Leave undefined (not '') when the wire has no URL, so the download link
      // omits `href` and renders inert instead of pointing at the current page.
      attachmentUrl: a.attachmentUrl ?? undefined,
      kind: a.kind,
    })),
    customDetails: mapCustomDetails(contract.customDetails),
    notes: contract.notes ?? '',
  };
};
